import React, { useCallback, useEffect, useState } from "react";
import { Caption1, Image, Label, Subtitle1, Text, mergeClasses } from "@fluentui/react-components";
import { useStyles } from "./menuItemDetailsStyles";
import { ApiException, CartItem, ICartItem, OnlineMenuItem } from "@dining/api-client";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { HelperUtils, appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { ConfirmationDialogProps } from "../../shared";
import { clearActiveCart } from "../../../common";
import { ConfirmationDialog } from "../../shared";
import { CartItemForm } from "../../Forms";

export const MenuItemDetails = () => {
  const navigate = useNavigate();
  const context = useAppStore((state) => state.context);
  const appInsights = useAppStore((state) => state.appInsights);
  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "MenuItemDetails",
    });
  }, [appInsights, context]);
  const params = useParams();
  const { cafeId, stationId, editItemId } = params;
  const [client, cart, setLoading] = useAppStore((state) => [
    state.client,
    state.cart,
    state.setLoading,
  ]);
  const { value: currentCart, setCart, abandonCart } = cart;

  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  const loaderData = useLoaderData() as { menuItem: OnlineMenuItem };
  const { menuItem } = loaderData;
  const helperUtils: HelperUtils = HelperUtils.getInstance();
  const allergenStr = helperUtils.getAllergenStr(menuItem);
  const pricingStr = helperUtils.getPricingStr(menuItem.pricing);
  const calorieStr = helperUtils.getCalorieStr(menuItem);
  const dietaryLabelStr = helperUtils.getDietaryLabelStr(menuItem);
  const dietaryLabelAccessibleStr = helperUtils.getDietaryLabelAccessibleStr(menuItem);

  const styles = useStyles();
  const editItemsArray = currentCart?.items?.find((item) => item.id === editItemId);
  const customInstructions = editItemsArray?.customInstruction;
  const isAvailable = menuItem?.isAvailableOnline;
  useEffect(() => {
    document.title = menuItem?.name + " - Microsoft Dining";
  }, [menuItem]);
  const onSubmit = useCallback(
    async (values: ICartItem) => {
      cafeId !== currentCart?.cafeId && currentCart! ? emptyOtherCart() : onSubmitForm(values);
    },
    [cafeId, client, currentCart],
  );

  const onSubmitForm = useCallback(
    async (values: ICartItem) => {
      // TODO: Move this logic to the store (cart state slice);
      try {
        setLoading(true);
        let response;
        if (editItemId) {
          response = await client?.editItemInCafeCart(
            cafeId!,
            editItemId!,
            undefined,
            CartItem.fromJS(values),
          );
          if (response) {
            setCart(response);
            navigate(`/cafe/${cafeId}/checkout`);
          }
        } else {
          response = await client?.addItemToCafeCart(cafeId!, undefined, CartItem.fromJS(values));
          if (response) {
            setCart(response);
            navigate(`/cafe/${cafeId}/station/${stationId}`, { replace: true });
          }
        }
      } catch (error) {
        if (ApiException.isApiException(error)) {
          const apiException = error as ApiException;
          appInsights.trackException(
            { exception: apiException },
            {
              method: "addItemToCafeCart",
              cafeId,
              values,
            },
          );
        } else {
          appInsights.trackException(
            { exception: error as Error },
            {
              method: "addItemToCafeCart",
              cafeId,
              values,
            },
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [cafeId, client, currentCart],
  );

  const getContentStr = useCallback(
    async (cafeId: string | undefined) => {
      let cafeName: string | undefined;
      let contentStr: string | undefined = "";
      try {
        const response = await client?.getCafeById(cafeId!, undefined);
        cafeName = response?.name;
      } catch (error) {
        if (ApiException.isApiException(error)) {
          const apiException = error as ApiException;
          appInsights.trackException(
            { exception: apiException },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        } else {
          appInsights.trackException(
            { exception: error as Error },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        }
      }

      contentStr = `You currently have an active cart from ${currentCart?.cafeName}. 
                      Do you wish to remove ${
                        currentCart && currentCart?.items?.length > 1 ? "those items" : "that item"
                      } 
                      and add ${menuItem.name} from ${
                        cafeName ? cafeName : ""
                      } to your cart instead?`;
      return contentStr;
    },
    [cafeId, client, currentCart],
  );

  const emptyOtherCart = useCallback(async () => {
    const contentStr = await getContentStr(cafeId);

    setDialogProps({
      content: contentStr,
      open: true,
      title: clearActiveCart,

      confirmAction: async () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.AddItemToCartConfirmed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "MenuItemDetails",
          },
        );
        abandonCart();
        setDialogProps(undefined);
      },
      dismissAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.ItemOrderDismissed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "MenuItemDetails",
          },
        );
        setDialogProps(undefined);
      },
    });
  }, [setDialogProps, abandonCart, appInsights, context, currentCart]);

  return (
    <div className={styles.container} data-testid="menuItemDetails">
      <main className={styles.main}>
        <div className={styles.subMainContainer}>
          {dialogProps && cafeId !== currentCart?.cafeId && currentCart! && (
            <ConfirmationDialog {...dialogProps} />
          )}
          <div className={styles.detailsContainer}>
            {menuItem.imageUrl && (
              <Image
                src={menuItem.imageUrl}
                onError={(e) => {
                  // Retry on failing to load.
                  e.currentTarget.src = menuItem.imageUrl || "";
                }}
                alt={menuItem.name}
                shape={"rounded"}
                className={styles.image}
              />
            )}
            <div
              className={mergeClasses(styles.details, styles.padding)}
              role="button"
              aria-label={
                (menuItem.description?.trim()
                  ? menuItem.description.replace(/\+/g, " and ")
                  : menuItem.name) +
                (dietaryLabelAccessibleStr ? " " + dietaryLabelAccessibleStr : "") +
                "." +
                (pricingStr
                  ? pricingStr +
                    (calorieStr ? " and this item contains " + calorieStr + " calories" : "")
                  : calorieStr
                    ? "this item contains " + calorieStr + " calories"
                    : "")
              }
            >
              <div className={styles.details}>
                <Subtitle1>
                  {menuItem.name}
                  {dietaryLabelStr && (
                    <Text
                      className={styles.dietaryLabel}
                      aria-hidden="true"
                      data-testid="dietaryLabel"
                    >
                      {" "}
                      {dietaryLabelStr}
                    </Text>
                  )}
                </Subtitle1>
                <Label className={styles.description}>{menuItem.description}</Label>
                <Caption1 className={styles.allergen}>{allergenStr}</Caption1>
              </div>
              <div className={styles.priceCaloriesContainer}>
                {pricingStr && <Subtitle1 data-testid="menuItemPrice">{pricingStr}</Subtitle1>}
                {calorieStr &&
                  (pricingStr ? (
                    <Label className={styles.calories} data-testid="menuItemCaloriesWithMidDot">
                      &middot; {calorieStr}
                    </Label>
                  ) : (
                    <Label className={styles.calories} data-testid="menuItemCalories">
                      {calorieStr}
                    </Label>
                  ))}
              </div>
            </div>
          </div>
          <div className={styles.optionGroupContainer}>
            <CartItemForm
              item={editItemsArray ? editItemsArray : menuItem}
              onSubmit={onSubmit}
              editId={editItemId}
              isAvailable={isAvailable}
              customFoodInstructions={customInstructions}
            />
          </div>
        </div>
      </main>
    </div>
  );
};
